import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const PostEventAnalysis = () => (
  <Page
    title="Post-Event Analysis"
    intro="Post-event analysis (PEA) lives on the Post-event tab of the Planning workspace. It brings together patient outcomes, staff and client feedback, themed learning, and follow-up actions, and produces a client-ready PDF report."
  >
    <Section title="Completing the analysis">
      <Step number="1" title="Write the narrative">
        Fill in the <strong>Summary of Event</strong>,{' '}
        <strong>Staff Feedback</strong>, and{' '}
        <strong>Client Feedback</strong>. Where staff left end-of-shift
        feedback, <strong>View Shift Feedback</strong> lists their comments
        with a copy button. Client feedback mostly arrives by itself: responses
        to the event record's <strong>Client Feedback</strong> request links
        are appended to the client feedback field automatically (see{' '}
        <DocLink
          docsPage
          url="/event-planning/event-overview"
          text="Event Record Overview"
        />
        ), and feedback submitted through the client portal lands there too.
      </Step>
      <Step number="2" title="Work through the themes">
        Nine fixed themes — Event Planning, Resources, Personnel,
        Communications, Response Times, Build &amp; Tear-down, Crowd Safety,
        Staff Welfare, and NHS Impact — each take an{' '}
        <strong>Analysis</strong> and <strong>Recommendations</strong> entry.
        Themes left blank are dropped from the report.
      </Step>
      <Step number="3" title="Assign follow-up actions">
        <strong>Add an Action</strong> records what needs doing, how, by
        when, and which event planner is responsible. Actions are marked
        complete from their detail view, which stamps who completed them and
        when.
      </Step>
      <Step number="4" title="Check the patient summary">
        <strong>Patient Contacts</strong> shows the total patients, hospital
        transports, and how many injuries or illnesses actually occurred at
        the event, drawn automatically from the ePCRs and paper records linked
        to the event. A warning appears if any patient records are still in
        draft — chase those before generating the report.
      </Step>
      <Info title="Where the &quot;occurred at the event&quot; figures come from">
        Every Event-type ePCR and Fast Track record asks{' '}
        <strong>Did it occur at the event?</strong> (Yes / No / Unknown) next
        to the linked event. Answering it separates injuries and illnesses
        caused at the event from patients who arrived with something that
        happened elsewhere. Records left unanswered, paper records, and
        Welfare records are counted as &quot;not recorded&quot;.
      </Info>
      <Step number="5" title="Choose what goes in the Post Event Report">
        Under <strong>Post Event Report</strong>, every section is a card
        listing the figures it would print, each with its value shown (for
        example <em>Shifts filled: 65 of 66 (98.5%)</em> or{' '}
        <em>Punctuality: 75.9% on time</em>). Untick a whole section, or just
        the figures you don&apos;t want to send. A figure you leave out is also
        left out of the executive summary, the key findings and the day
        pages. Anything marked <strong>Check</strong> may read badly (unfilled
        shifts, a low on-time rate, complaints, records still in draft), so
        look at it before the report goes out. Sections and figures with
        nothing to show are greyed out with the reason.
      </Step>
      <Info title="Presenting complaints">
        The clinical summary always groups complaints into{' '}
        <strong>presenting categories</strong> (such as Trauma &amp; Injuries
        or Cardiorespiratory) with a table of the most common complaints. For
        events with day pages, choose <strong>Categories and each
        complaint</strong> under Clinical activity to add every individual
        complaint, with its count, to each day&apos;s page.
      </Info>
      <Step number="6" title="Generate the report">
        For events over more than one day, keep <strong>Day by day</strong>{' '}
        ticked for a comparison of days and a page for each day. Optionally
        flag <strong>Mark PEA as complete</strong> (remember to Save Changes),
        then click <strong>Generate report</strong>. Your choices are
        remembered for the event on this computer, and{' '}
        <strong>Reset to defaults</strong> puts everything back. Generation
        is desktop-only because the charts are drawn in the browser.
      </Step>
      <Info title="Every report is kept">
        Each generated report is saved automatically to the event&apos;s{' '}
        <strong>Documents</strong> as <em>Post Event Report - v2 - 14 Jul
        2026</em>, visible to planners only, so you always have a copy of
        exactly what was sent. The version number goes up each time. Your
        section choices are remembered for the event on this computer.
      </Info>
    </Section>

    <Section title="What the report contains">
      <Bullets>
        <Bullet>
          A cover page with the event, client and venue details, version and
          classification, headline numbers and a contents list.
        </Bullet>
        <Bullet>
          <strong>Executive summary</strong>: headline figures, key findings
          written from the data, the medical plan forecast against what
          actually happened, and your debrief summary.
        </Bullet>
        <Bullet>
          <strong>Event and resourcing</strong>: staff deployed by role,
          shifts filled, planned and clocked hours, punctuality, and vehicles.
        </Bullet>
        <Bullet>
          <strong>Clinical activity</strong>: presentations by hour, reason for
          attending, outcome, age, sex and record type, plus the most common
          presenting complaints.
        </Bullet>
        <Bullet>
          <strong>Day by day</strong> (multi-day events): days run 05:00 to
          05:00, so late-night patients count towards the evening before.
        </Bullet>
        <Bullet>
          <strong>Control room performance</strong> (when a control room is
          linked): incident mix and priority; median, mean and 90th centile
          for call to dispatch, response (call to first on scene), time on
          scene and job cycle; and jobs and hours per call sign.
        </Bullet>
        <Bullet>
          <strong>Treatment locations</strong>: patients, length of stay, peak
          occupancy and an occupancy chart for each location.
        </Bullet>
        <Bullet>
          <strong>NHS impact</strong>, <strong>medicines administered</strong>,{' '}
          <strong>health and safety concerns</strong> and, optionally, the
          control room&apos;s <strong>significant decisions</strong>.
        </Bullet>
        <Bullet>
          <strong>Feedback</strong>: patient compliments and complaints from
          records linked to the event, organiser ratings and comments, and
          (optionally) anonymous staff comments.
        </Bullet>
        <Bullet>
          <strong>Lessons and recommendations</strong>, the{' '}
          <strong>action plan</strong>, and a methodology and data-quality
          appendix.
        </Bullet>
      </Bullets>
      <Info title="Patient confidentiality">
        The report is written for the event organiser and Safety Advisory
        Group, so patient information appears as totals only. There are no
        names, dates of birth or clinical notes. Patient comments are only
        quoted if the patient agreed their feedback may be shared.
      </Info>
    </Section>

    <Section title="Availability and access">
      <Info title="Tier 2 feature">
        Post-event analysis requires a tier 2 subscription. The tab is
        available throughout — you don't have to wait for the event to end,
        though the patient summary only fills as records are completed.
      </Info>
      <Info title="Who can do this?">
        Editing the PEA needs the <strong>Event Planning</strong> team, the
        same as the rest of the event record. Client feedback arrives from
        the event's Client Feedback request links or from approved client
        portal contacts.
      </Info>
    </Section>
  </Page>
)

export default PostEventAnalysis
